import React, { useRef, useCallback } from 'react'
import { IconButton } from '../../../ui'
import { MiniPill } from '../../../ui/MiniPill/MiniPill.jsx'

/**
 * TypedBulletList — bullet list where each row carries a required type chip.
 *
 * Used by Asks, Focus, Support, Stuck cards (shape-shift from chip-multi-context
 * to typed bullet list). Each bullet has { id, text, type }.
 *
 * `typeOptions` = [{ id, label, tone }] — shown as cycling MiniPill on each row.
 * `required` — when true, a row without a type shows an error ring.
 */

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function generateId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `b_${crypto.randomUUID().slice(0, 8)}`
    : `b_${Math.random().toString(36).slice(2, 10)}`
}

function TypeChip({ value, options, onChange, disabled }) {
  function cycle() {
    if (disabled) return
    const idx = options.findIndex((o) => o.id === value)
    const next = options[(idx + 1) % options.length]
    onChange(next.id)
  }

  if (!value) {
    return (
      <button
        type="button"
        onClick={cycle}
        disabled={disabled}
        style={styles.emptyChip}
        aria-label="Set type"
      >
        + type
      </button>
    )
  }

  const opt = options.find((o) => o.id === value)
  return (
    <MiniPill
      tone={opt?.tone}
      as="button"
      type="button"
      onClick={cycle}
      disabled={disabled}
      aria-label={`Type: ${opt?.label ?? value}. Click to change.`}
      style={{ cursor: disabled ? 'default' : 'pointer', border: 'none', background: 'none' }}
    >
      {opt?.label ?? value}
    </MiniPill>
  )
}

export function TypedBulletList({
  items = [],
  onChange,
  typeOptions = [],
  maxItems = 15,
  maxChars = 200,
  placeholder = '',
  disabled = false,
  required = false,
}) {
  const inputRefs = useRef([])
  const displayItems = items.length === 0 ? [{ id: generateId(), text: '', type: null }] : items

  const handleTextChange = useCallback((index, text) => {
    const next = displayItems.map((item, i) => (i === index ? { ...item, text } : item))
    onChange({ items: next })
  }, [displayItems, onChange])

  const handleTypeChange = useCallback((index, type) => {
    const next = displayItems.map((item, i) => (i === index ? { ...item, type } : item))
    onChange({ items: next })
  }, [displayItems, onChange])

  const handleAdd = useCallback(() => {
    if (displayItems.length >= maxItems) return
    const next = [...displayItems, { id: generateId(), text: '', type: null }]
    onChange({ items: next })
    setTimeout(() => inputRefs.current[displayItems.length]?.focus(), 0)
  }, [displayItems, onChange, maxItems])

  const handleRemove = useCallback((index) => {
    if (displayItems.length <= 1) {
      onChange({ items: [{ id: generateId(), text: '', type: null }] })
    } else {
      onChange({ items: displayItems.filter((_, i) => i !== index) })
    }
  }, [displayItems, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (displayItems.length < maxItems) {
        const next = [...displayItems]
        next.splice(index + 1, 0, { id: generateId(), text: '', type: null })
        onChange({ items: next })
        setTimeout(() => inputRefs.current[index + 1]?.focus(), 0)
      }
    } else if (e.key === 'Backspace' && displayItems[index].text === '' && displayItems.length > 1) {
      e.preventDefault()
      onChange({ items: displayItems.filter((_, i) => i !== index) })
      setTimeout(() => inputRefs.current[Math.max(0, index - 1)]?.focus(), 0)
    }
  }, [displayItems, onChange, maxItems])

  return (
    <div style={styles.wrap}>
      {displayItems.map((item, i) => (
        <div key={item.id ?? i} style={styles.row}>
          <span style={styles.prefix} aria-hidden>—</span>
          <textarea
            ref={(el) => { inputRefs.current[i] = el; autoResize(el) }}
            value={item.text}
            rows={1}
            onChange={(e) => { handleTextChange(i, e.target.value); autoResize(e.target) }}
            onKeyDown={(e) => handleKeyDown(e, i)}
            placeholder={disabled ? '' : placeholder}
            disabled={disabled}
            maxLength={maxChars}
            aria-label={`Item ${i + 1}`}
            style={{
              ...styles.input,
              borderColor: required && item.text && !item.type ? 'var(--rust-500)' : 'transparent',
            }}
          />
          <TypeChip
            value={item.type}
            options={typeOptions}
            onChange={(type) => handleTypeChange(i, type)}
            disabled={disabled}
          />
          {!disabled && (
            <IconButton ariaLabel="Remove item" onClick={() => handleRemove(i)} tabIndex={-1}>×</IconButton>
          )}
        </div>
      ))}
      {!disabled && displayItems.length < maxItems && (
        <button type="button" onClick={handleAdd} style={styles.addBtn}>
          + add
        </button>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 4 },
  row: { display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 },
  prefix: { color: 'var(--color-text-faint)', flexShrink: 0, fontSize: 12, lineHeight: '1.5', paddingTop: 2 },
  input: {
    flex: '1 1 0', minWidth: 0, background: 'transparent', border: 'none',
    outline: 'none', fontSize: 12, color: 'var(--color-text-primary)',
    fontFamily: 'inherit', padding: '2px 0', resize: 'none', overflow: 'hidden',
    lineHeight: 1.5,
  },
  emptyChip: {
    fontSize: 9, fontWeight: 500, padding: '2px 6px',
    background: 'transparent', color: 'var(--color-text-muted)',
    border: '0.5px dashed var(--color-line)', borderRadius: 4,
    textTransform: 'uppercase', letterSpacing: '0.03em',
    cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
  },
  addBtn: {
    fontSize: 11, color: 'var(--color-accent)', background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px 0', textAlign: 'left', fontFamily: 'inherit',
  },
}
